/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { useWindowSize } from '@brands/hooks';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { addClinicalData } from '@brands/services/clinical_data/addClinicalData';
import { listClinicalData } from '@brands/services/clinical_data/listClinicalData';
import { MedicalHistory } from '@brands/services/clinical_data/types/types';
import { updateClinicalData } from '@brands/services/clinical_data/updateClinicalData';
import { selectAuth } from '@brands/store/selectors/auth';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { Button } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import Container from '../../Components/Container/Container';
import { Loading } from '../../Components/LoadingSpinner/Loading';
import { useAppSelector } from '../../hooks/useReduxHook';
import RemoveItemModal from '../Dashboard/Components/RemoveItemModal/RemoveItemModal';
import AddPharmacy from './Components/AddPharmacy';
import SearchForPharmacy from './Components/FindPharmacy';
import { PharmacySubmit } from './Components/types';
import styles from './styles.module.scss';

const Pharmacy = (): JSX.Element => {
  const screenSize = useWindowSize();
  const { userInfo } = useAppSelector(selectAuth);
  const [render, setRender] = useState(true);
  const [pharmacyName, setPharmacyName] = useState('');
  const [pharmacyAddress, setPharmacyAddress] = useState('');
  const [pharmacyPhoneNumber, setPharmacyPhoneNumber] = useState('');
  const [findPharmacy, setFindPharmacy] = useState(false);
  const [addPharmacy, setAddPharmacy] = useState(false);
  const [objectId, setObjectId] = useState<string | null>(null);
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory | null>(null);
  const [removeItemModal, setRemoveItemModal] = useState(false);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const AddNewPharmacy = async (newPharmacy: PharmacySubmit): Promise<void> => {
    try {
      if (!objectId) {
        await addClinicalData('medical_history', {
          user_id: Number(userInfo.id),
          under_care_of_physician: null,
          conditions_treated: null,
          taking_medication: null,
          medications: null,
          allergic_to_medications: null,
          medications_allergies: null,
          using_substances: [],
          interested_in_stopping: null,
          height: '',
          weight: '',
          pharmacy_name: newPharmacy.pharmacy_name,
          pharmacy_street_address: newPharmacy.pharmacy_street_address,
          pharmacy_city: newPharmacy.pharmacy_city,
          pharmacy_state: newPharmacy.pharmacy_state,
          pharmacy_zip_code: newPharmacy.pharmacy_zip_code,
          pharmacy_phone_number: newPharmacy.pharmacy_phone_number,
        });
      } else {
        await updateClinicalData('medical_history', objectId, {
          under_care_of_physician: medicalHistory?.under_care_of_physician ?? null,
          conditions_treated: medicalHistory?.conditions_treated ?? null,
          taking_medication: medicalHistory?.taking_medication ?? null,
          medications: medicalHistory?.medications ?? null,
          allergic_to_medications: medicalHistory?.allergic_to_medications ?? null,
          medications_allergies: medicalHistory?.medications_allergies ?? null,
          using_substances: medicalHistory?.using_substances ?? [],
          interested_in_stopping: medicalHistory?.interested_in_stopping ?? null,
          height: medicalHistory?.height ?? '',
          weight: medicalHistory?.weight ?? '',
          height_cm: medicalHistory?.height_cm ?? null,
          weight_kg: medicalHistory?.weight_kg ?? null,
          height_unit: medicalHistory?.height_unit,
          weight_unit: medicalHistory?.weight_unit,
          ...newPharmacy,
        });
      }
      await getFormAnswers(Number(userInfo?.id));
      setAddPharmacy(false);
      setFindPharmacy(false);
      showSnackbar(`You’ve successfully added ${newPharmacy.pharmacy_name} to your saved pharmacies`, 'success');
    } catch (error) {
      showSnackbar(`Error: ${error}`, 'error');
    }
  };

  const removePharmacy = async (): Promise<void> => {
    if (objectId) {
      await updateClinicalData('medical_history', objectId, {
        under_care_of_physician: medicalHistory?.under_care_of_physician ?? null,
        conditions_treated: medicalHistory?.conditions_treated ?? null,
        taking_medication: medicalHistory?.taking_medication ?? null,
        medications: medicalHistory?.medications ?? null,
        allergic_to_medications: medicalHistory?.allergic_to_medications ?? null,
        medications_allergies: medicalHistory?.medications_allergies ?? null,
        using_substances: medicalHistory?.using_substances ?? [],
        interested_in_stopping: medicalHistory?.interested_in_stopping ?? null,
        height: medicalHistory?.height ?? '',
        weight: medicalHistory?.weight ?? '',
        height_cm: medicalHistory?.height_cm ?? null,
        weight_kg: medicalHistory?.weight_kg ?? null,
        height_unit: medicalHistory?.height_unit,
        weight_unit: medicalHistory?.weight_unit,
        pharmacy_name: '',
        pharmacy_street_address: '',
        pharmacy_city: '',
        pharmacy_state: '',
        pharmacy_zip_code: '',
        pharmacy_phone_number: '',
      });
      await getFormAnswers(Number(userInfo?.id));
      setRemoveItemModal(false);
    }
  };

  const getFormAnswers = useCallback(async (createdByPatientId: number | undefined) => {
    setRender(false);
    const { medical_history } = await listClinicalData(createdByPatientId ?? 0);
    setMedicalHistory(medical_history ?? null);
    if (medical_history) {
      setObjectId(medical_history.id.toString());
    }

    setPharmacyName(medical_history?.pharmacy_name || '');

    setPharmacyAddress(
      medical_history?.pharmacy_street_address && medical_history?.pharmacy_city
        ? `${medical_history.pharmacy_street_address}\n${medical_history.pharmacy_city}, ${medical_history.pharmacy_state} ${medical_history.pharmacy_zip_code}`
        : '',
    );
    setPharmacyPhoneNumber(medical_history?.pharmacy_phone_number || '');
    setRender(true);
  }, []);

  useEffect(() => {
    (async () => {
      setRender(false);
      setPharmacyAddress('');
      await getFormAnswers(Number(userInfo?.id));
      setRender(true);
    })();
  }, [userInfo]);

  if (findPharmacy) {
    return (
      <Container
        patientDashboard
        childClassName={styles.dashboard}
        containerStyle={{
          justifyContent: screenSize.width >= 1289 ? 'center' : 'start',
          overflowY: screenSize.width >= 1289 ? 'hidden' : 'scroll',
        }}
      >
        <SearchForPharmacy
          setFindPharmacy={setFindPharmacy}
          setAddPharmacy={setAddPharmacy}
          AddNewPharmacy={AddNewPharmacy}
        />
      </Container>
    );
  }

  if (addPharmacy) {
    return (
      <Container
        patientDashboard
        childClassName={styles.dashboard}
        containerStyle={{
          justifyContent: screenSize.width >= 1289 ? 'center' : 'start',
          overflowY: screenSize.width >= 1289 ? 'hidden' : 'scroll',
        }}
      >
        <AddPharmacy
          setFindPharmacy={setFindPharmacy}
          setAddPharmacy={setAddPharmacy}
          AddNewPharmacy={AddNewPharmacy}
        />
      </Container>
    );
  }

  if (render && !findPharmacy) {
    return (
      <Container
        patientDashboard
        childClassName={styles.dashboard}
        containerStyle={{
          justifyContent: screenSize.width >= 1289 ? 'center' : 'start',
          overflowY: screenSize.width >= 1289 ? 'hidden' : 'scroll',
        }}
      >
        <div className={styles.pharmacyContainer}>
          <h2 className="fs-unmask">My pharmacy</h2>
          {pharmacyName && pharmacyAddress ? (
            <div className={`fs-exclude  ${styles.pharmacyInfo}`} data-testid="saved-pharmacy-div">
              <div className={`fs-exclude  ${styles.pharmacyInfoLeft}`}>
                <span className={styles.sectionContentRowDetailsName} data-testid="pharmacy-name">
                  {pharmacyName}
                </span>
                <span
                  className={styles.sectionContentRowDetailsAddress}
                  style={{ whiteSpace: 'pre-line' }}
                  data-testid="pharmacy-address"
                >
                  {pharmacyAddress}
                </span>
                {pharmacyPhoneNumber && pharmacyPhoneNumber !== '' && (
                  <span className={styles.sectionContentRowDetailsAddress} data-testid="pharmacy-phone">
                    📞 {pharmacyPhoneNumber}
                  </span>
                )}
              </div>
              <div className={`fs-exclude  ${styles.pharmacyInfoRight}`}>
                <Button
                  variant="text"
                  color="error"
                  onClick={() => setRemoveItemModal(true)}
                  data-testid="saved-pharmacy-remove"
                >
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className={`fs-unmask  ${styles.noPharmacy}`}>
              <span>You haven’t added any pharmacies yet</span>
              <Button
                variant="contained"
                color="primary"
                data-testid="pharmacy-add-pharmacy"
                onClick={() => setFindPharmacy(true)}
              >
                Add a pharmacy
              </Button>
            </div>
          )}
          {removeItemModal && (
            <RemoveItemModal
              isOpen={removeItemModal}
              toggleModal={setRemoveItemModal}
              itemName={`${pharmacyName}`}
              removeItem={removePharmacy}
            />
          )}
          <GenericSnackbar
            open={snackbar.isOpen}
            message={snackbar.message}
            type={snackbar.type}
            onClose={closeSnackbar}
          />
        </div>
      </Container>
    );
  }

  return <Loading fullScreen />;
};

export default Pharmacy;
